/**
 * BlogCard Component
 * Displays a concise preview of a blog post with image, title, description, and date
 * For use in blog listings, related posts, and other content collections
 */

import React from "react";
import Link from "next/link";
import { format, isValid, parseISO } from "date-fns";
import { BlogCardProps } from "@/types/contentful/blog-card";
import styles from "./BlogCard.module.css";


// Define allowed variants explicitly
type BlogCardVariant = "default" | "wide" | "default-fullwidth";

/**
 * BlogCard - Reusable card component for blog post preview
 * 
 * @param post - Blog post data (can be full BlogPost or minimal BlogCardData)
 * @param className - Optional CSS class for custom styling
 * @param variant - Card variant ('default', 'wide', or 'default-fullwidth')
 * @returns A card component with image, title, description, and date
 */
const BlogCard: React.FC<BlogCardProps & { variant?: BlogCardVariant }> = ({ 
  post, 
  className,
  currentLocale,
  variant = "default"
  
  
}) => {
  
  
  // Extract image URL from post with fallback
  const imageUrl = post.heroImage?.url || "/images/placeholder-blog.jpg";
  
  // Format the publication date
  const formattedDate = React.useMemo(() => {
    if (!post.publishedDate) return "";
    
    try {
      const parsedDate = parseISO(post.publishedDate);
      return isValid(parsedDate) ? format(parsedDate, "MMM dd, yyyy") : "";
    } catch (e) {
      return "";
    }
  }, [post.publishedDate]);
  
  // Generate post URL: Use post.url if available, otherwise construct from slug
  const postUrl = "url" in post && post.url ? post.url : `/${currentLocale}/travel-blog/${post.slug}`;
  
  // Determine CSS class based on variant
  const getCardClassName = (cardVariant: BlogCardVariant) => {
    switch (cardVariant) {
      case "wide":
        return styles.cardWide;
      case "default-fullwidth":
        return styles.cardDefaultFullwidth;
      case "default":
      default:
        return styles.card;
    }
  };
  const cardClassName = getCardClassName(variant);

  return (
    <Link
      href={postUrl}
      {...(typeof currentLocale === "string" ? { locale: currentLocale } : {})}
      key={post.id}
      className={styles.link}
      data-testid={`blog-card-link-${post.id}`}
    >
      <article 
        className={`${cardClassName} ${className || ""}`} 
        data-testid={`blog-card-${post.id}`}
      >
        <div 
          className={styles.imageContainer}
          style={{
            backgroundImage: `url(${imageUrl})`
          }}
          title={post.heroImage?.altText || `${post.title} featured image`}
          data-testid={`blog-card-image-${post.id}`}
          role="img"
          aria-label={post.heroImage?.altText || `${post.title} featured image`}
         />
        <div className={styles.content}>
          <div>
            <h3 
              className={styles.title}
              data-testid={`blog-card-title-${post.id}`}
            >
              {post.title}
            </h3>
            <p 
              className={styles.description}
              data-testid={`blog-card-summary-${post.id}`}
            >
              {post.summary}
            </p>
          </div>
          {formattedDate && (
            <time 
              className={styles.date}
              dateTime={post.publishedDate}
              data-testid={`blog-card-date-${post.id}`}
            >
              {formattedDate}
            </time>
          )}
        </div>
      </article>
    </Link>
  );
};

export default BlogCard;